import type { FeatureContext } from './featureContext';
import { initSwarmBoard } from '../ui/eventBoard';
import { createActiveEdge, swarmCue } from '../audio/defineCue';
import { swingCue, ratDeathCue } from '../audio/combatCue';
import { GIANT_RAT_BODY } from '../sim/content';
import { proximityCueVolume } from './cueVolume';

// Loudness ceiling for a rat's death squeal at distance 0, mixed a touch below
// doors so a nearby kill punctuates without drowning the room (proximity-scaled
// per kill against DEFAULT_SOUND_CONFIG.maxTiles, like footsteps).
const RAT_SQUEAL_VOLUME = 0.7;

/** Injectable seams for unit tests; defaults are the real modules. */
export interface SwarmInstallDeps {
  initSwarmBoard?: typeof initSwarmBoard;
}

/**
 * The Rat Swarm presentation (PRD #222): the live event board (Slice F,
 * ADR-0121), the event-boundary audio cues, the rat die-clip bookkeeping that
 * feeds the movement core's pursuit (Slice E, ADR-0120), and the stomper's own
 * swing grunt.
 */
export function installSwarm(ctx: FeatureContext, deps: SwarmInstallDeps = {}): void {
  // The live event board: a top-centre overlay fed by the server's RatSwarmStatus
  // pushes - top stompers, rats remaining, and a countdown it ticks down between
  // pushes from the render loop. Appears on the start push, disappears on the end
  // push, so the normal view returns by itself.
  const eventBoard = (deps.initSwarmBoard ?? initSwarmBoard)();

  // The event-boundary edge latch (the shared discipline, src/audio/defineCue.ts):
  // seed silently on the first status push of each connection - except that a
  // player already online when the swarm starts gets their first push as `active`
  // (the server seeds a status only mid-event), which sounds the start scream; the
  // cost is a mid-event (re)login also hearing it once, fitting for walking into a
  // bank full of rats. Only a real flip sounds a cue after that.
  const swarmEdge = createActiveEdge();
  ctx.events.on('connection', () => swarmEdge.reset());

  // Reset the board on every (re)connection (the PR #243 reconnect guard): the
  // shared handle outlives the socket, the board only hides on an `active:false`
  // push, and the server only seeds a status at login while an event is RUNNING -
  // so a reconnect spanning the event's end (or a mid-event server restart, which
  // wipes the in-memory swarm) would otherwise strand the board on screen with the
  // countdown pinned at 0:00. If the event is genuinely still running, the
  // mid-event seed push arrives in the same login flush and repaints it immediately.
  ctx.events.on('welcome', () => eventBoard.reset());

  ctx.events.on('ratSwarmStatus', (msg) => {
    eventBoard.update(msg);
    // Event-boundary cue on a real active<->inactive edge: the scream when the
    // swarm floods in, the "protect the bank" horn when it ends. A first-ever
    // push that is already inactive is silently seeded (nothing to end - never
    // happens in practice, the server only seeds while active, but the latch
    // keeps it honest).
    if (swarmEdge.observe(msg.active)) {
      ctx.audio.play(swarmCue(msg.active));
    }
  });

  ctx.events.on('actionAnimation', (msg) => {
    // The local player's stomp swing: a random human "oomph" grunt picked by the
    // player's body sex. Full volume - it's the player's own effort sound, not a
    // sound positioned in the world. Only the local player's swing grunts (we know
    // only our own sex); playback routes through the same `AudioManager`, so the
    // master mute governs it.
    const identity = ctx.identity();
    if (msg.clip === 'attack' && msg.entityId === ctx.playerId() && identity) {
      ctx.audio.play(swingCue(identity.sex));
    }
    // A `die` is the earliest kill signal this client gets (Slice E, ADR-0120):
    // the corpse entity deliberately lingers ~1s so the clip can play, and its
    // delta `removed` only arrives after that. Mark it untargetable now, and if
    // it is the rat *this* player is pursuing, cancel the chase on the spot -
    // someone else won the race (PRD story 34); waiting for the removal would
    // ghost-chase the corpse. Pruned from `dyingRats` once the entity leaves
    // the snapshot (below).
    const source = ctx.world()?.get(msg.entityId)?.renderable;
    if (msg.clip === 'die' && source?.body === GIANT_RAT_BODY) {
      ctx.state.dyingRats.add(msg.entityId);
      ctx.state.ratDied(msg.entityId);
      // A random rat death squeal, proximity-scaled to the local player like a
      // footstep (rats die all over the map) - dropped when out of earshot. The
      // corpse still lingers in the snapshot here, so its tile is available.
      const volume = proximityCueVolume(ctx.world(), ctx.playerId(), msg.entityId, RAT_SQUEAL_VOLUME);
      const squeal = ratDeathCue(volume);
      if (squeal) ctx.audio.play(squeal);
    }
  });

  // Retire corpse tracking for rats that have actually left the world (their
  // ~1s die-clip linger is over), so the set never grows across events.
  ctx.events.on('snapshot', (world) => {
    for (const id of ctx.state.dyingRats) {
      if (!world.get(id)) ctx.state.dyingRats.delete(id);
    }
  });

  // Tick the board's countdown between status pushes: server-anchored on each
  // push, client-ticked here. A no-op while no event runs.
  ctx.onFrame(() => eventBoard.sync());
}
